// frontend/src/services/guardAuth.service.ts
import axios from 'axios';
import { config } from '../config/config';
import { ChangePasswordRequest, GuardLoginRequest, GuardLoginResponse, GuardUser, VerifyMfaRequest } from '../types/guardAuth';

const GUARD_TOKEN_KEY = 'guard_token';
const GUARD_USER_KEY = 'guard_user';
const GUARD_AUTH_EVENT = 'guard-auth-change';

class GuardAuthService {
  private baseUrl = `${config.API_URL}/guard-auth`;

  private notifyAuthChange() {
    window.dispatchEvent(new Event(GUARD_AUTH_EVENT));
  }

  async login(credentials: GuardLoginRequest): Promise<GuardLoginResponse> {
    const response = await axios.post<GuardLoginResponse>(
      `${this.baseUrl}/login`,
      credentials
    );

    // MFA is mandatory. The login response holds only the challenge token;
    // the real JWT is issued by /verify-mfa. Don't store anything yet.
    if (response.data.mfa_required) {
      return response.data;
    }

    this.storeSession(credentials.username, response.data);
    return response.data;
  }

  async verifyMfa(username: string, request: VerifyMfaRequest): Promise<GuardLoginResponse> {
    const response = await axios.post<GuardLoginResponse>(
      `${this.baseUrl}/verify-mfa`,
      request
    );
    this.storeSession(username, response.data);
    return response.data;
  }

  private storeSession(username: string, data: GuardLoginResponse): void {
    localStorage.setItem(GUARD_TOKEN_KEY, data.access_token);
    localStorage.setItem(GUARD_USER_KEY, JSON.stringify({
      username,
      user_type: data.user_type,
      display_name: data.display_name,
      default_location_id: data.default_location_id,
      managed_yard_ids: data.managed_yard_ids ?? null,
    }));
    this.notifyAuthChange();
  }

  logout(): void {
    localStorage.removeItem(GUARD_TOKEN_KEY);
    localStorage.removeItem(GUARD_USER_KEY);
    this.notifyAuthChange();
  }

  getToken(): string | null {
    return localStorage.getItem(GUARD_TOKEN_KEY);
  }

  getUser(): GuardUser | null {
    const userStr = localStorage.getItem(GUARD_USER_KEY);
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  isGuard(): boolean {
    const user = this.getUser();
    return user?.user_type === 'guard';
  }

  async changePassword(request: ChangePasswordRequest): Promise<void> {
    const token = this.getToken();
    await axios.post(
      `${this.baseUrl}/change-password`,
      request,
      { headers: { Authorization: `Bearer ${token}` } }
    );
  }
}

const guardAuthService = new GuardAuthService();
export default guardAuthService;
